import React, { useState } from 'react';
import { FaCalendarAlt, FaFilter, FaChevronDown, FaChevronUp } from 'react-icons/fa';

/**
 * Componente para filtros avançados de estatísticas
 */
const StatsFilters = ({ onFilterChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [timeRange, setTimeRange] = useState('all');
  const [positionFilter, setPositionFilter] = useState('all');
  const [outcomeFilter, setOutcomeFilter] = useState('all');

  // Aplicar filtros quando qualquer valor mudar
  const handleFilterChange = (filterType, value) => {
    let newFilters = {};
    
    switch (filterType) {
      case 'timeRange':
        setTimeRange(value);
        newFilters = { timeRange: value, position: positionFilter, outcome: outcomeFilter };
        break;
      case 'position':
        setPositionFilter(value);
        newFilters = { timeRange, position: value, outcome: outcomeFilter };
        break;
      case 'outcome':
        setOutcomeFilter(value);
        newFilters = { timeRange, position: positionFilter, outcome: value };
        break;
      default:
        return;
    }
    
    // Notificar componente pai sobre mudança nos filtros
    if (onFilterChange) {
      onFilterChange(newFilters);
    }
  };

  // Obter rótulo para exibição do filtro de tempo atual
  const getTimeRangeLabel = () => {
    switch (timeRange) {
      case 'today': return 'Today';
      case 'yesterday': return 'Yesterday';
      case 'week': return 'This Week';
      case 'month': return 'This Month';
      case 'all': return 'All Time';
      default: return 'All Time';
    }
  };

  return (
    <div className="bg-[#0d0d0d] rounded-lg border border-[#7900ff]/20 mb-4 overflow-hidden">
      {/* Cabeçalho do filtro (sempre visível) */}
      <div 
        className="flex justify-between items-center p-3 cursor-pointer hover:bg-[#1a1a1a] transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <FaFilter className="text-[#7900ff]" />
          <span className="font-medium">Filters</span>
        </div>
        
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-400 hidden sm:inline">
            {getTimeRangeLabel()}
            {positionFilter !== 'all' && ` • ${positionFilter === 'up' ? 'UP' : 'DOWN'}`}
            {outcomeFilter !== 'all' && ` • ${outcomeFilter === 'won' ? 'Won' : 'Lost'}`}
          </span>
          {isExpanded ? <FaChevronUp size={12} /> : <FaChevronDown size={12} />}
        </div>
      </div>
      
      {/* Conteúdo expandido dos filtros */}
      {isExpanded && (
        <div className="p-3 border-t border-[#7900ff]/20 space-y-4">
          {/* Filtro de período */}
          <div>
            <h4 className="text-sm text-gray-400 mb-2 flex items-center gap-1">
              <FaCalendarAlt size={12} />
              Time Period
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
              {[
                { value: 'today', label: 'Today' },
                { value: 'yesterday', label: 'Yesterday' },
                { value: 'week', label: 'This Week' },
                { value: 'month', label: 'This Month' },
                { value: 'all', label: 'All Time' }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => handleFilterChange('timeRange', option.value)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    timeRange === option.value
                      ? 'bg-[#7900ff] text-white'
                      : 'bg-[#7900ff]/20 text-gray-400 hover:bg-[#7900ff]/30'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Filtro de posição */}
          <div>
            <h4 className="text-sm text-gray-400 mb-2">Position</h4>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'all', label: 'All' },
                { value: 'up', label: 'UP' },
                { value: 'down', label: 'DOWN' }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => handleFilterChange('position', option.value)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    positionFilter === option.value
                      ? option.value === 'up'
                        ? 'bg-green-500 text-white'
                        : option.value === 'down'
                          ? 'bg-red-500 text-white'
                          : 'bg-[#7900ff] text-white'
                      : 'bg-[#7900ff]/20 text-gray-400 hover:bg-[#7900ff]/30'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          
          {/* Filtro de resultado */}
          <div>
            <h4 className="text-sm text-gray-400 mb-2">Outcome</h4>
            <div className="grid grid-cols-3 gap-2">
              {[
                { value: 'all', label: 'All' },
                { value: 'won', label: 'Won' },
                { value: 'lost', label: 'Lost' }
              ].map(option => (
                <button
                  key={option.value}
                  onClick={() => handleFilterChange('outcome', option.value)}
                  className={`px-3 py-1 rounded-full text-xs font-medium transition-colors ${
                    outcomeFilter === option.value
                      ? option.value === 'won'
                        ? 'bg-green-500 text-white'
                        : option.value === 'lost'
                          ? 'bg-red-500 text-white'
                          : 'bg-[#7900ff] text-white'
                      : 'bg-[#7900ff]/20 text-gray-400 hover:bg-[#7900ff]/30'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StatsFilters;
